import type { Client } from '@urql/vue';

import type { DictionaryPhrases } from '@/lib/dictPlugin';
import type { InitialState, SitecoreDictionary } from '@/sitecoreState';

import GETTRANSLATIONSCACHED from './graphql/getTranslationsCached.gql';
import { createSsrDictionaryCache } from './ssrDictionaryCache';

const ssrDictionaryCache = createSsrDictionaryCache();

const getTranslationsFromGraphQL = async (
  client: Client,
  lang: string,
  dictionary: SitecoreDictionary,
  host: string,
): Promise<DictionaryPhrases> => {
  const { data: translationData, error } = await client
    .query(GETTRANSLATIONSCACHED, {
      lang,
      timestamp: dictionary.timestamp,
      host,
      dict: dictionary.id,
    })
    .toPromise();
  if (error || !translationData) {
    throw error ?? new Error('no translation data');
  }
  return JSON.parse(translationData.getTranslationsCached.translations);
};

const dictionaryUrl = (lang: string, dictionary: SitecoreDictionary, baseUrl: string): string =>
  `${baseUrl}/api/dictionary/${encodeURIComponent(
    dictionary.id,
  )}?lang=${lang}&timestamp=${encodeURIComponent(dictionary.timestamp)}`;

const logFailure = (error: any, url?: string): void => {
  console.error(
    'Could not load translations',
    ['ETIMEDOUT', 'ECONNABORTED'].includes(error?.code) ? error?.code : error,
    url,
  );
};

const getTranslationsLocally = async (
  lang: string,
  dictionary: SitecoreDictionary,
  baseUrl: string,
): Promise<DictionaryPhrases> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);
  try {
    const response = await fetch(dictionaryUrl(lang, dictionary, baseUrl), {
      signal: controller.signal,
    });
    if (!response.ok) {
      const error: any = new Error(`Failed to fetch dictionary: ${response.status}`);
      error.code = 'ECONNABORTED';
      throw error;
    }
    return await response.json();
  } catch (error: any) {
    if (error.name === 'AbortError') {
      const timeoutError: any = new Error('Dictionary request timed out');
      timeoutError.code = 'ETIMEDOUT';
      throw timeoutError;
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
};

/**
 * Says the server has translations to render with, whichever way it got them - from the network,
 * from the SSR cache, or from the bundled fallback. server.ts reads it off the state before that
 * state is trimmed, and treats a configured dictionary *without* it as a degraded render: markup
 * built with every key resolving to '', which the client will not reproduce. See shouldRehydrate.
 * `stale` marks translations of an older timestamp, served because the endpoint was saturated: the
 * markup reads right but must not be cached or hydrated either.
 */
const markFetched = (
  initialState: InitialState | undefined,
  translations: DictionaryPhrases,
  stale = false,
): void => {
  const dictionary = initialState?.sitecore?.settings?.site?.dictionary;
  if (IS_SSR && dictionary && Object.keys(translations).length) {
    dictionary.ssrFetched = true;
    if (stale) {
      dictionary.ssrStale = true;
    }
  }
};

const setupDictionary = async (
  lang: string,
  client: Client,
  initialState: InitialState | undefined,
  baseUrl: string,
): Promise<DictionaryPhrases> => {
  try {
    if (IS_SHOWROOM || IS_PAGE_RENDERER) {
      // @ts-ignore
      let fallbackDictionary = (await import('../data/dictionary/de.yaml'))
        .default as DictionaryPhrases;
      if (lang === 'en') {
        const enDictionary = (await import('../data/dictionary/en.json'))
          .default as DictionaryPhrases;
        fallbackDictionary = {
          ...fallbackDictionary,
          ...enDictionary,
        };
      }

      markFetched(initialState, fallbackDictionary);

      return fallbackDictionary;
    } else {
      const dictionary: SitecoreDictionary | null | undefined =
        initialState?.sitecore?.settings?.site?.dictionary;
      if (!dictionary) {
        return {};
      }

      if (IS_SSR) {
        const { translations, status } = await ssrDictionaryCache.load(
          { baseUrl, lang, id: dictionary.id, timestamp: dictionary.timestamp },
          () => getTranslationsLocally(lang, dictionary, baseUrl),
          (error) => logFailure(error, dictionaryUrl(lang, dictionary, baseUrl)),
        );
        markFetched(initialState, translations, status === 'stale');

        return translations;
      }

      const translations = await (APP_ENVIRONMENT === 'VM' ||
      initialState?.sitecore?.data?.isContentManagement ||
      window.location.pathname.startsWith('/sitecore')
        ? getTranslationsLocally(lang, dictionary, baseUrl)
        : getTranslationsFromGraphQL(client, lang, dictionary, baseUrl));

      markFetched(initialState, translations);

      return translations;
    }
  } catch (error: any) {
    logFailure(error);
    return {};
  }
};

export default setupDictionary;
